import { Form, FormValues, UploadFile } from 'antd';
import { RcFile } from 'antd/es/upload';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import FileUpload from '../../../../shared/components/file-upload/file-upload';
import Modal from '../../../../shared/components/modal/modal';
import { signRequestAlertId } from '../../../sign-requests/constants/constants';
import LazyModal, { LazyModalComponentProps } from '../../../ui/components/lazy-modal';
import { closeModal, setAlert } from '../../../ui/store/ui-slice';
import { useUploadArchiveFilesMutation } from '../../api/archive-api';
import { fileUploadModalId } from '../../constants/constants';

interface Props {
  requestId?: string;
}

const ArchiveFileUploadModalContent = ({
  isOpen,
  afterClose,
  requestId,
}: LazyModalComponentProps & Props): JSX.Element => {
  const { t } = useTranslation(['archive', 'common']);
  const dispatch = useDispatch();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploadArchiveFiles, { isLoading: isUploadArchiveFilesLoading }] = useUploadArchiveFilesMutation();
  const [form] = Form.useForm<{ files: RcFile[] }>();
  const [isUploading, setIsUploading] = useState(false);

  const closeFileUploadModal = () => {
    dispatch(closeModal(fileUploadModalId));
  };

  const validateFiles = (_: unknown, files?: RcFile[]) => {
    const invalidEmptyFile = files?.find(file => file.size === 0);
    const invalidLargeFile = files?.find(file => file.size >= 10 * 1024 * 1024);

    if (invalidEmptyFile) {
      return Promise.reject(new Error(t('errors.notBlank', { ns: 'common' })));
    }

    if (invalidLargeFile) {
      return Promise.reject(new Error(t('uploadFileModal.validation.uploadLimitReached')));
    }

    return Promise.resolve();
  };

  const onSubmit = (formData: FormValues<typeof form>) => {
    if (!requestId) {
      return;
    }
    setIsUploading(true);

    const filteredUploadedItems = fileList.filter(p => p.status !== 'done');
    const fileUploadPromises = (formData.files || []).map(file => {
      const isFileSizeAcceptable = file.size / 1024 / 1024 < 10;
      const fileListItem = fileList.find(({ uid }) => uid === file.uid);
      if (!isFileSizeAcceptable) {
        if (fileListItem) {
          fileListItem.status = 'error';
        }
        form.setFields([
          {
            name: 'files',
            errors: [t('uploadFileModal.validation.uploadLimitReached')],
          },
        ]);
        return { success: false, error: t('uploadFileModal.validation.uploadLimitReached') };
      }
      if (filteredUploadedItems.some(item => item.uid === file.uid)) {
        return uploadArchiveFiles({ file, requestId })
          .unwrap()
          .then(response => {
            if (fileListItem) {
              fileListItem.status = 'done';
            }
            return { success: true, response: response };
          })
          .catch(error => {
            if (fileListItem) {
              fileListItem.status = 'error';
              fileListItem.response = error.data.errors.content?.[0] || t('uploadFileModal.validation.failedUpload');
            }
            return { success: false, error: error.data.errors.content?.[0] };
          });
      }
    });

    Promise.all(fileUploadPromises)
      .then(results => {
        const hasFailures = results.some(result => result && !result.success);
        const error = results.find(result => result && !result.success);
        if (hasFailures) {
          const errorMessage = error && 'error' in error ? error.error : t('errors.unexpected', { ns: 'common' });

          form.setFields([
            {
              name: 'files',
              errors: [errorMessage],
            },
          ]);
        } else {
          closeFileUploadModal();
          dispatch(
            setAlert({
              id: signRequestAlertId,
              type: 'success',
              title: t('message.uploadSuccess'),
            }),
          );
        }
      })
      .finally(() => setIsUploading(false));
  };

  useEffect(() => {
    form.setFieldValue('files', fileList.map(file => file.originFileObj).filter(Boolean));
  }, [fileList, form]);

  return (
    <Modal
      title={t('uploadFileModal.title')}
      onCancel={() => !isUploadArchiveFilesLoading && closeFileUploadModal()}
      isLoading={isUploading}
      isOpen={isOpen}
      afterClose={afterClose}
      formId={fileUploadModalId}
    >
      <Form
        id={fileUploadModalId}
        form={form}
        initialValues={{
          files: fileList,
        }}
        layout="vertical"
        onFinish={onSubmit}
      >
        <Form.Item
          name="files"
          rules={[
            { required: true, message: t('uploadFileModal.validation.filesRequired') },
            { validator: validateFiles },
          ]}
        >
          <FileUpload
            fileUploadProps={{
              fileList,
              multiple: true,
              onChange: ({ fileList }) => {
                setFileList([...fileList]);
              },
              //   TODO: when BE api will be implemented, implement delete logic
              onRemove: file => {
                const index = fileList.indexOf(file);
                const newFileList = fileList.slice();
                newFileList.splice(index, 1);
                setFileList(newFileList);
              },
              beforeUpload: () => false,
            }}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const ArchiveFileUploadModal = () => {
  return <LazyModal modalId={fileUploadModalId} Component={ArchiveFileUploadModalContent} />;
};

export default ArchiveFileUploadModal;
